import { useEffect, useState } from 'react';
import BriefDocuments from '@/components/creative/BriefDocuments';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Send, Check, Copy, Link2, MapPin, Clock, X, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import PromptDialog from '@/components/ui/PromptDialog';
import {
  fetchShoot, patchShoot, patchCreative, fetchEditLock, sendScript, recordScriptDecision, talentTelegramCode, addCreativesToShoot, removeCreativeFromShoot,
  type Creative, type TalentCode,
} from '@/lib/queries';
import { useCan } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';
import BookCallDialog from '@/components/creative/BookCallDialog';
import NewMeetingDialog from '@/components/meetings/NewMeetingDialog';
import { dubai, fetchMeetings, MEETING_STATUS } from '@/lib/calls';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { isDesign } from '@/lib/steps';
import RichEditor from '@/components/ui/RichEditor';
import RichView, { htmlToPlain } from '@/components/ui/RichView';

const NO_DOCTOR = { label: 'No doctor needed', cls: 'bg-slate-700/40 text-slate-300' };

const STATUS: Record<string, { label: string; cls: string }> = {
  draft:             { label: 'Not sent',          cls: 'bg-slate-700/40 text-slate-300' },
  sent:              { label: 'Waiting for doctor', cls: 'bg-amber-500/15 text-amber-300' },
  approved:          { label: 'Doctor approved',    cls: 'bg-emerald-500/15 text-emerald-300' },
  changes_requested: { label: 'Changes requested',  cls: 'bg-red-500/15 text-red-300' },
};

export function shootLabel(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

export default function ShootCard({ creative }: { creative: Creative }) {
  const noShoot = creative.needs_shoot === false;
  const qc = useQueryClient();
  const { open, toggle } = useCollapsed('creative.shoot.open');
  const canPlan = useCan('shoots.plan');
  const shoot = useQuery({ queryKey: ['shoot', creative.id], queryFn: () => fetchShoot(creative.id) });
  const s = shoot.data;

  const [notes, setNotes] = useState('');
  const [talent, setTalent] = useState('');
  const [script, setScript] = useState('');
  const [scriptHtml, setScriptHtml] = useState('');
  const [code, setCode] = useState<TalentCode | null>(null);
  const [decide, setDecide] = useState<'approved' | 'changes_requested' | null>(null);
  const [pickShoot, setPickShoot] = useState('');
  const [booking, setBooking] = useState(false);
  const [meeting, setMeeting] = useState(false);
  // The doctor is part of the brief (api/app/edit_lock.py): the picker follows the lock,
  // and past the brief the owner or a manager says why before it changes.
  const lock = useQuery({ queryKey: ['editLock', creative.id], queryFn: () => fetchEditLock(creative.id) });
  const doctorOpen = !!lock.data && (lock.data.editable || lock.data.override);
  const [doctorReason, setDoctorReason] = useState<string | null>(null);
  // A call still pending on this script: shown as a chip, and it stops a second call.
  const calls = useQuery({ queryKey: ['meetings', 'open', creative.id], queryFn: () => fetchMeetings({ creative_id: creative.id, open: true }) });
  const pendingCall = (calls.data ?? []).find((m) => {
    const row = m.creatives.find((x) => x.creative_id === creative.id);
    return row && !row.filed_at && !row.decided_outside;
  });

  useEffect(() => {
    if (!s) return;
    setNotes(s.shoot_notes ?? ''); setTalent(s.talent_id ?? ''); setScript(s.script ?? ''); setScriptHtml(s.script_html ?? '');
    if (!pickShoot && s.eligible_shoots.length) setPickShoot(s.eligible_shoots[0].id);
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!s && (notes !== (s.shoot_notes ?? '') || talent !== (s.talent_id ?? '') || script !== (s.script ?? '') || scriptHtml !== (s.script_html ?? ''));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['shoot', creative.id] });
    qc.invalidateQueries({ queryKey: ['creatives', creative.id] });
    qc.invalidateQueries({ queryKey: ['shoots'] });
    qc.invalidateQueries({ queryKey: ['comments', creative.id] });
    // The progress bar reads the tracker. Without this it keeps saying the piece
    // is waiting on a doctor for another thirty seconds after you settle it.
    qc.invalidateQueries({ queryKey: ['track', creative.id] });
  };

  const doctorChanged = !!s && talent !== (s.talent_id ?? '');
  const save = useMutation({
    mutationFn: (reason?: string) => patchShoot(creative.id, { shoot_notes: notes, talent_id: talent, script, ...(scriptHtml !== (s?.script_html ?? '') ? { script_html: scriptHtml } : {}), ...(reason ? { reason } : {}) }),
    onSuccess: () => { toast.success('Saved'); setDoctorReason(null); invalidate(); qc.invalidateQueries({ queryKey: ['editLock', creative.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // Saved on the spot rather than with the rest of the form: it changes what the
  // whole card shows, so leaving it pending behind a Save button reads as broken.
  const setNeedsDoctor = useMutation({
    mutationFn: (v: boolean) => patchShoot(creative.id, { script_needs_doctor: v }),
    onSuccess: (_d, v) => { toast.success(v ? 'A doctor approves this one' : 'No doctor on this one'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // The shoot switch lives here, next to the doctor switch, so a piece can be taken off a
  // shoot where its shoot is shown. It used to sit only in Edit creative, and not at all
  // for a static (Aziz, 2026-10-04: could not find it for a design).
  const design = isDesign(creative.creative_type);
  const setNeedsShoot = useMutation({
    mutationFn: (v: boolean) => patchCreative(creative.id, { needs_shoot: v }),
    onSuccess: (_d, v) => { toast.success(v ? (design ? 'Photoshoot first, then the design' : 'Filmed on a shoot day') : (design ? 'No photoshoot: straight to the design' : 'No shoot: straight to the editor')); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const send = useMutation({
    mutationFn: () => sendScript(creative.id),
    onSuccess: (r) => { toast.success(`Script sent to ${r.talent_name} on Telegram`); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const record = useMutation({
    mutationFn: ({ decision, feedback, mentions }: { decision: 'approved' | 'changes_requested'; feedback?: string; mentions?: string[] }) =>
      recordScriptDecision(creative.id, { decision, feedback, mentions }),
    onSuccess: () => { toast.success('Recorded'); setDecide(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const getCode = useMutation({
    mutationFn: () => talentTelegramCode(creative.client_id, talent),
    onSuccess: (r) => setCode(r),
    onError: (e) => toast.error(errMsg(e)),
  });
  const join = useMutation({
    mutationFn: () => addCreativesToShoot(pickShoot, [creative.id]),
    onSuccess: () => { toast.success('Added to the shoot'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const leave = useMutation({
    mutationFn: () => removeCreativeFromShoot(s!.shoot!.id, creative.id),
    onSuccess: () => { toast.success('Taken off the shoot'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (shoot.isLoading || !s) return <Card><div className="text-muted text-sm">Loading shoot...</div></Card>;

  const status = s.script_needs_doctor ? (STATUS[s.script_status] ?? STATUS.draft) : NO_DOCTOR;
  const talentOpts = [{ value: '', label: 'No doctor yet' }, ...s.talents.map((t) => ({ value: t.id, label: t.name + (t.telegram_linked ? '' : ' (no Telegram)') }))];
  // a doctor set before the clinic list was edited still shows as the current choice
  if (talent && !s.talents.some((t) => t.id === talent)) talentOpts.push({ value: talent, label: (s.talent_name || 'Current doctor') + ' (not in the clinic list)' });
  const chosen = s.talents.find((t) => t.id === talent);
  const canSend = canPlan && !dirty && !!s.has_script && !!s.talent_id && s.talent_telegram_linked && s.script_status !== 'sent';

  return (
    <Card>
      <div className={cn('flex flex-wrap items-center justify-between gap-2', open && 'mb-3')}>
        <button type="button" onClick={toggle} aria-expanded={open}
          className="font-medium text-muted-strong text-sm inline-flex items-center gap-2">
          <CollapseChevron open={open} /><Camera size={14} /> Shoot and script
        </button>
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {pendingCall && (
            <Link to={`/meetings/${pendingCall.id}`} data-testid="call-chip"
              className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide inline-flex items-center gap-1', MEETING_STATUS[pendingCall.status].cls)}>
              <CalendarClock size={10} />
              {pendingCall.status === 'booked' ? `Call ${dubai(pendingCall.starts_at)}` : MEETING_STATUS[pendingCall.status].label}
            </Link>
          )}
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', status.cls)}>{status.label}</span>
        </span>
      </div>

      {open && (<>
      {noShoot && (
        <div className="rounded-lg border border-slate-800 bg-bg px-3 py-2 mb-3 text-xs text-muted" data-testid="no-shoot-note">
          {design
            ? 'No photoshoot for this piece: it is designed from what exists. One design step, then the final review.'
            : "No shoot for this piece: it is made from existing material. The script and the doctor's approval still apply; once approved it goes to the editor."}
        </div>
      )}
      {/* At a glance, for everyone */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mb-4">
        <div className="rounded-lg bg-bg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><Clock size={10} /> Shoot</div>
          <div className="text-muted-strong">
            {s.shoot ? <Link to={`/shoots/${s.shoot.id}`} className="text-accent-400 hover:underline">{shootLabel(s.shoot.shoot_at)}</Link> : 'Not on a shoot yet'}
          </div>
        </div>
        <div className="rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><MapPin size={10} /> Where</div><div className="text-muted-strong">{s.shoot?.location || (s.shoot ? 'To be confirmed' : '')}{!s.shoot && <span className="text-muted">Set on the shoot</span>}</div></div>
        <div className="rounded-lg bg-bg px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted">Doctor</div><div className="text-muted-strong">{!s.script_needs_doctor ? 'Not needed' : <>{s.talent_name || 'Not picked'}{s.talent_name && (s.talent_telegram_linked ? ' · on Telegram' : ' · not on Telegram')}</>}</div></div>
      </div>

      {/* Shoot membership: only an approved script can join */}
      <div className={cn('rounded-lg border px-3 py-2 text-xs mb-4 space-y-2', s.shoot ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-800 bg-bg')}>
        {s.shoot ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-muted-strong">On shoot <Link to={`/shoots/${s.shoot.id}`} className="text-accent-400 hover:underline">{s.shoot.title}</Link>
              {s.script_needs_doctor && s.script_status !== 'approved' && <span className="ml-2 text-red-300">script no longer approved, send it again before the shoot</span>}
            </div>
            {canPlan && <Button size="sm" variant="secondary" icon={<X size={12} />} onClick={() => leave.mutate()} disabled={leave.isPending}>Take off the shoot</Button>}
          </div>
        ) : canPlan ? (
          s.can_join ? (
            s.eligible_shoots.length ? (
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1"><Select label="Add to a shoot" value={pickShoot} onChange={setPickShoot}
                  options={s.eligible_shoots.map((x) => ({ value: x.id, label: `${shootLabel(x.shoot_at)} · ${x.title}${x.location ? ' · ' + x.location : ''}` }))} /></div>
                <Button size="sm" onClick={() => join.mutate()} disabled={!pickShoot || join.isPending} icon={<Camera size={12} />}>Add to shoot</Button>
              </div>
            ) : (
              <div className="text-muted-strong">{s.script_needs_doctor ? 'Script approved.' : 'Ready to shoot.'} No shoot planned for {s.client_name} yet: <Link to="/shoots" className="text-accent-400 hover:underline">create one on the Shoots page</Link>.</div>
            )
          ) : (
            <div className="text-muted">{s.script_needs_doctor ? 'Joins a shoot once the doctor approves the script' : 'Not ready for a shoot yet'} ({s.join_blocker}).</div>
          )
        ) : (
          <div className="text-muted">Not scheduled yet. It joins a shoot once the script is approved.</div>
        )}
      </div>

      {canPlan ? (
        <div className="space-y-3">
          {/* Not every piece has a doctor: a coordinator piece, or a general video for
              the clinic. Without this the approval step sits at "waiting for doctor"
              for ever and the piece can never join a shoot or open its board. */}
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="needs-shoot-card">
            <input type="checkbox" className="accent-accent-400 mt-1" data-testid="needs-shoot-card-input"
              checked={!noShoot} disabled={setNeedsShoot.isPending}
              onChange={(e) => setNeedsShoot.mutate(e.target.checked)} />
            <span>
              <span className="text-muted-strong">{design ? 'Photoshoot needed' : 'Needs a shoot'}</span>
              <span className="block text-xs text-muted">{design
                ? (noShoot ? 'Designed from what exists: one design step, then the review.' : 'Photographed first: photoshoot, raws and the art director\'s raws review, then the design.')
                : (noShoot ? 'Made from existing material: no shoot, straight to the editor once the script is approved.' : 'Filmed on a shoot day: shoot, raws and the raws review are in the plan.')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="needs-doctor">
            <input type="checkbox" className="accent-accent-400 mt-1" data-testid="needs-doctor-input"
              checked={!s.script_needs_doctor} disabled={setNeedsDoctor.isPending}
              onChange={(e) => setNeedsDoctor.mutate(!e.target.checked)} />
            <span>
              <span className="text-muted-strong">No doctor on this one</span>
              <span className="block text-xs text-muted">{s.script_needs_doctor
                ? 'A doctor approves the script before it can go on a shoot.'
                : 'A coordinator piece or a general clinic video. Nobody approves the script, and it can join a shoot straight away.'}</span>
            </span>
          </label>
          {s.script_needs_doctor && (
            <div>
              <Select label={`Doctor (${s.talents.length} on this clinic)`} value={talent} onChange={setTalent} options={talentOpts} searchable
                disabled={!doctorOpen} />
              {lock.data && !doctorOpen && (
                <p className="mt-1 text-[11px] text-muted" data-testid="shoot-doctor-locked">
                  {lock.data.locked ? lock.data.message : `The doctor is part of the brief: ${lock.data.owners.join(' and ') || 'its content creator'} picks it.`}
                </p>
              )}
              {doctorChanged && ['sent', 'approved', 'changes_requested'].includes(s.script_status) && (
                <p className="mt-1 text-[11px] text-amber-300">The script goes back to draft: the new doctor has not seen it.</p>
              )}
            </div>
          )}
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs text-muted">Notes for the crew</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} dir="auto" data-testid="shoot-notes"
              placeholder="Wardrobe, props, patient consent, parking..." rows={3}
              ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(480, Math.max(84, el.scrollHeight + 2))}px`; } }}
              className={cn('rounded-lg border border-slate-800 bg-bg px-3 py-2 text-sm text-muted-strong placeholder:text-muted/50 leading-relaxed resize-y focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40', BIDI_CLS)} />
          </label>
          <div className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs text-muted">Script {s.script_status !== 'draft' && dirty && script !== (s.script ?? '') ? '(editing resets the approval)' : ''}</span>
            <RichEditor html={scriptHtml} plain={script} minHeight={180} testid="script-editor" placeholder="What the doctor says, shot by shot."
              onChange={(h) => { setScriptHtml(h); setScript(htmlToPlain(h)); }} />
          </div>
          {s.script_feedback && (
            <div className={cn('rounded-lg px-3 py-2 text-xs border',
              s.script_status === 'approved' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5')}
              data-testid="script-note">
              <div className={cn('text-[10px] uppercase tracking-wide mb-1',
                s.script_status === 'approved' ? 'text-emerald-300' : 'text-red-300')}>
                {s.script_status === 'approved' ? 'How the doctor approved it' : 'Doctor asked for changes'}
              </div>
              <div className="text-muted-strong whitespace-pre-wrap">{s.script_feedback}</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" onClick={() => (doctorChanged && lock.data?.locked && lock.data.override ? setDoctorReason('') : save.mutate(undefined))} disabled={!dirty || save.isPending} icon={<Check size={12} />}>Save</Button>
            {s.script_needs_doctor && (<>
            <Button size="sm" variant="secondary" onClick={() => send.mutate()} disabled={!canSend || send.isPending} icon={<Send size={12} />}
              title={dirty ? 'Save first' : !s.has_script ? 'Write the script first' : !s.talent_id ? 'Pick the doctor first' : !s.talent_telegram_linked ? 'The doctor is not on Telegram yet' : ''}>
              {s.script_status === 'sent' ? 'Sent, waiting' : s.script_status === 'draft' ? `Send to ${chosen ? chosen.name : 'doctor'} on Telegram` : 'Send again'}
            </Button>
            {/* You are often standing with the doctor and never send the script at all.
                This used to appear only after sending, so there was no way to record
                an approval that happened in the room. */}
            {s.has_script && s.script_status !== 'approved' && (
              <>
                <button className="text-xs text-muted hover:text-emerald-300 underline min-h-[36px]" data-testid="record-approved"
                  onClick={() => setDecide('approved')}>Doctor approved it</button>
                <button className="text-xs text-muted hover:text-red-300 underline min-h-[36px]" data-testid="record-changes"
                  onClick={() => setDecide('changes_requested')}>Doctor wants changes</button>
              </>
            )}
            {/* Go through this script, and any others for the clinic, on one call. */}
            {s.has_script && s.script_status !== 'approved' && !pendingCall && (
              <Button size="sm" variant="secondary" icon={<CalendarClock size={12} />} onClick={() => setBooking(true)}
                disabled={dirty} title={dirty ? 'Save first' : ''} data-testid="book-call">Book a script call</Button>
            )}
            {/* Any meeting about this piece: with the clinic or just the team (2026-09-27). */}
            <Button size="sm" variant="ghost" icon={<CalendarClock size={12} />} onClick={() => setMeeting(true)} data-testid="book-meeting">Book a meeting</Button>
            </>)}
          </div>
          {chosen && !chosen.telegram_linked && (
            <div className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-xs space-y-2">
              <div className="text-muted-strong inline-flex items-center gap-1"><Link2 size={12} /> {chosen.name} is not on Telegram yet.</div>
              {!code ? (
                <Button size="sm" variant="secondary" onClick={() => getCode.mutate()} disabled={getCode.isPending}>Get a link code for {chosen.name}</Button>
              ) : (
                <div className="space-y-1">
                  <div className="text-muted">Send the doctor these two lines (WhatsApp is fine):</div>
                  <div className="font-mono text-muted-strong break-all">1. Open {code.bot_url}  2. Send: {code.command}</div>
                  <Button size="sm" variant="secondary" icon={<Copy size={12} />}
                    onClick={() => { navigator.clipboard?.writeText(`Please open ${code.bot_url} and send this message to the bot: ${code.command}`); toast.success('Copied'); }}>
                    Copy the instructions
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {s.shoot_notes && <div className="text-xs"><div className="text-[10px] uppercase tracking-wide text-muted">Notes</div><div className="text-muted-strong whitespace-pre-wrap">{s.shoot_notes}</div></div>}
          {s.script ? (
            <div className="text-xs"><div className="text-[10px] uppercase tracking-wide text-muted mb-1">Script</div><RichView html={s.script_html} plain={s.script} className="text-sm text-muted-strong" testid="script-view" /></div>
          ) : <div className="text-xs text-muted">No script yet.</div>}
          <BriefDocuments creative={creative} compact />
          {s.script_feedback && (
            <div className="text-xs">
              <div className={cn('text-[10px] uppercase tracking-wide',
                s.script_status === 'approved' ? 'text-emerald-300' : 'text-red-300')}>
                {s.script_status === 'approved' ? 'How the doctor approved it' : 'Doctor asked for changes'}
              </div>
              <div className="text-muted-strong whitespace-pre-wrap">{s.script_feedback}</div>
            </div>
          )}
        </div>
      )}

      </>)}

      <PromptDialog
        open={doctorReason !== null}
        title="Change the doctor after the brief locked"
        message={`${lock.data?.message ?? ''} Say why the doctor changes now. It goes in the log and the content creator is told.`}
        placeholder="e.g. Dr Beta is on leave, the clinic moved it to Dr Alpha"
        multiline maxLength={500}
        confirmLabel="Change the doctor"
        onCancel={() => setDoctorReason(null)}
        onConfirm={(v) => save.mutate(v.trim())}
      />
      <PromptDialog
        open={decide !== null}
        title={decide === 'approved' ? 'Record: doctor approved the script' : 'Record: doctor wants changes'}
        message={decide === 'approved'
          ? 'How did they approve it, and what did they say? In the room, on WhatsApp, by phone. Add anything they asked for: the steps to show, the details to mention, what to leave out. The crew and the editor read this.'
          : 'What do they want changed? Their words, as close as possible.'}
        placeholder={decide === 'approved'
          ? 'Approved in the clinic with Dr Shaima. She wants the three steps shown in order, the recovery time said out loud, and no price.'
          : 'Their words, as close as possible'}
        multiline maxLength={4000}
        mentions
        confirmLabel="Record"
        onConfirm={(v, named) => record.mutate({ decision: decide ?? 'approved', feedback: v, mentions: named })}
        onCancel={() => setDecide(null)}
      />
      <BookCallDialog creativeId={creative.id} open={booking} onClose={() => { setBooking(false); calls.refetch(); }} />
      {meeting && (
        <NewMeetingDialog open onClose={() => { setMeeting(false); calls.refetch(); }} prefill={{
          kind: 'client', clientIds: [creative.client_id], title: `${creative.code} ${creative.title}`.slice(0, 200),
          items: [{ kind: 'creative', id: creative.id, label: `${creative.code} ${creative.title}` }],
        }} />
      )}
    </Card>
  );
}
